import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  attachBullets,
  attachHeadline,
  estimateExplanation,
  ESTIMATE_DISCLAIMER,
  RENT_ASSISTANCE_CURRENT_RATESET,
  type AttachCheckOutput,
  type CraSituation,
  type HousingArrangement,
  type PrimaryPaymentStatus,
  type RentAssistanceOutput,
} from "@aucash/rules/rent-assistance";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, MoneyInput, Segmented } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// rent-assistance — Commonwealth Rent Assistance (CRA) estimator + attach-check,
// surfaced stateless-v1 (PLAN arch decision #1). One screen, two modes over the
// rules module's two surfaces; the result renders INLINE (no save-by-id, no auth
// gate, no persistence). Each compute is a stateless `calc.rentAssistance.compute`
// query — input -> output, no round-trip to a stored row. Copy comes verbatim
// from the rules layer's copy helpers so the wording can never drift from the
// API/SEO surfaces (validation §3: information, not advice).
//
// Module-specific guardrails held at this surface:
//  - Output is a PERSONALISED SUMMARY / estimate, never a Services Australia
//    determination (the rules-layer disclaimer + copy say so explicitly).
//  - No "you're owed" / "you should claim"; the attach-check stays strictly
//    qualitative ("CRA MAY apply — here's the official step").
//  - CRA is a government supplement: NO financial-product/credit handoff
//    anywhere on this screen (the rules module has no `handoff` field).
//  - Date-keyed: figures resolve the in-force CRA rate set on the API side
//    (RENT_ASSISTANCE_CURRENT_RATESET is used here only for the static label /
//    situation rows shown before a compute) — never a hard-coded dollar figure.

type Mode = "estimate" | "attach-check";

const MODE_OPTIONS: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: "estimate", label: "Estimate" },
  { value: "attach-check", label: "Eligibility check" },
];

const MODE_COPY: Record<Mode, { eyebrow: string; title: string; blurb: string }> = {
  estimate: {
    eyebrow: "Services Australia · CRA",
    title: "Estimate your Rent Assistance",
    blurb:
      "Commonwealth Rent Assistance is paid at 75c for every $1 of rent above a minimum threshold, up to a per-situation maximum. Enter your fortnightly rent and situation to see an estimate from the current Services Australia rates.",
  },
  "attach-check": {
    eyebrow: "Services Australia · CRA",
    title: "Could Rent Assistance apply to you?",
    blurb:
      "Rent Assistance is never a standalone payment — it attaches to a qualifying Centrelink payment or to Family Tax Benefit Part A (the route many working families miss). This is a quick, qualitative check of whether it may apply.",
  },
};

const SITUATION_OPTIONS: ReadonlyArray<{ value: CraSituation; label: string }> = [
  { value: "single_no_children", label: "Single" },
  { value: "single_sharer_no_children", label: "Single sharer" },
  { value: "couple_no_children", label: "Couple" },
  { value: "single_with_children", label: "Single + kids" },
  { value: "couple_with_children", label: "Couple + kids" },
];

const PAYMENT_OPTIONS: ReadonlyArray<{
  value: PrimaryPaymentStatus;
  label: string;
}> = [
  { value: "qualifying_income_support", label: "Centrelink payment" },
  { value: "ftb_part_a", label: "Family Tax Benefit A" },
  { value: "none", label: "None" },
  { value: "unsure", label: "Not sure" },
];

const HOUSING_OPTIONS: ReadonlyArray<{
  value: HousingArrangement;
  label: string;
}> = [
  { value: "private_rental", label: "Private rental" },
  { value: "public_housing", label: "Public housing" },
  { value: "board_or_lodging", label: "Board / lodging" },
  { value: "other", label: "Other" },
];

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.rentAssistance.compute.query>
>;

export default function RentAssistanceIndex(): JSX.Element {
  const router = useRouter();

  const [mode, setMode] = useState<Mode>("estimate");

  // estimate fields
  const [situation, setSituation] = useState<CraSituation>("single_no_children");
  const [fortnightlyRent, setFortnightlyRent] = useState("");

  // attach-check fields
  const [paymentStatus, setPaymentStatus] =
    useState<PrimaryPaymentStatus>("ftb_part_a");
  const [housing, setHousing] = useState<HousingArrangement>("private_rental");
  const [payingRentAboveMinimum, setPayingRentAboveMinimum] = useState(true);
  const [hasReportedRent, setHasReportedRent] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const onModeChange = (next: Mode): void => {
    setMode(next);
    setResult(null);
    setSubmitError(null);
    setErrors({});
  };

  const buildInput = ():
    | { input: Parameters<typeof trpc.calc.rentAssistance.compute.query>[0]["input"] }
    | null => {
    if (mode === "attach-check") {
      return {
        input: {
          mode: "attach-check",
          paymentStatus,
          housing,
          payingRentAboveMinimum,
          hasReportedRent,
        },
      };
    }
    return {
      input: {
        mode: "estimate",
        situation,
        fortnightlyRent: num(fortnightlyRent),
      },
    };
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (mode === "estimate" && fortnightlyRent.trim() === "")
      next.rent = "Enter the rent you pay per fortnight.";
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    const payload = buildInput();
    if (Object.keys(next).length > 0 || payload === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.rentAssistance.compute
      .query(payload)
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the check.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  const copy = MODE_COPY[mode];

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Rent Assistance" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>{copy.eyebrow}</Eyebrow>
            <Text style={styles.title}>{copy.title}</Text>
            <Text style={styles.subtitle}>{copy.blurb}</Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="What do you want to do?">
            <Segmented<Mode>
              options={MODE_OPTIONS}
              value={mode}
              onChange={onModeChange}
              testIDPrefix="mode"
            />
          </Field>

          {mode === "estimate" ? (
            <>
              <Field label="Your situation">
                <Segmented<CraSituation>
                  options={SITUATION_OPTIONS}
                  value={situation}
                  onChange={setSituation}
                  testIDPrefix="situation"
                />
              </Field>
              <Field
                label="Rent you pay per fortnight"
                help="The rent you actually pay each fortnight. Rent Assistance starts once rent is above a minimum threshold for your situation."
                error={errors.rent}
              >
                <MoneyInput
                  value={fortnightlyRent}
                  onChange={setFortnightlyRent}
                  testID="input-fortnightly-rent"
                  accessibilityLabel="Rent you pay per fortnight"
                />
              </Field>
            </>
          ) : (
            <>
              <Field
                label="What payment do you receive?"
                help="Rent Assistance attaches to a qualifying Centrelink payment or to Family Tax Benefit Part A — it is never paid on its own."
              >
                <Segmented<PrimaryPaymentStatus>
                  options={PAYMENT_OPTIONS}
                  value={paymentStatus}
                  onChange={setPaymentStatus}
                  testIDPrefix="payment"
                />
              </Field>
              <Field label="Your housing arrangement">
                <Segmented<HousingArrangement>
                  options={HOUSING_OPTIONS}
                  value={housing}
                  onChange={setHousing}
                  testIDPrefix="housing"
                />
              </Field>
              <Field label="Is your rent above the minimum threshold?">
                <Checkbox
                  checked={payingRentAboveMinimum}
                  onChange={setPayingRentAboveMinimum}
                  label="Yes — I pay more than the minimum rent threshold for my situation."
                />
              </Field>
              <Field label="Have you told Services Australia your current rent?">
                <Checkbox
                  checked={hasReportedRent}
                  onChange={setHasReportedRent}
                  label="Yes — Services Australia holds my current rent details."
                />
              </Field>
            </>
          )}

          <Button
            label={submitting ? "Checking…" : "See your summary →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="rent-assistance-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer (rules-layer ESTIMATE_DISCLAIMER) sits at the TOP, before
 * any number / verdict (validation §3.2 placement rule). The result is a
 * PERSONALISED SUMMARY, never a Services Australia determination. */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const lines = linesFor(result);
  const [headline, ...body] = lines;

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{ESTIMATE_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your summary · CRA</Eyebrow>
        <Chip
          label={result.mode === "estimate" ? "Estimate" : "Eligibility"}
          tone="est"
        />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline}</Text>
        {body.map((line, i) => (
          <Text key={i} style={styles.bodyText}>
            {line}
          </Text>
        ))}
      </Card>

      <Section title="Where these come from">
        <Text style={styles.bodyText}>
          These figures use the Services Australia / DSS Commonwealth Rent
          Assistance rates ({result.periodLabel}, last verified{" "}
          {result.sourcesVerifiedAt}). Rent Assistance rates index twice a year
          (20 March and 20 September) — always check servicesaustralia.gov.au.
          This is a personalised summary, not a Services Australia assessment.
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · CRA rate set{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

/** Pick the rules-layer copy helper for the result's mode. All wording comes
 * from the rules package — no inline figures, no advice framing. The estimate
 * helper already embeds the formatted dollar figures + the disclaimer (we drop
 * the trailing disclaimer line here since it renders at the top of the block). */
function linesFor(result: ComputeResult): string[] {
  if (result.mode === "estimate") {
    const out = result.output as RentAssistanceOutput;
    const lines = estimateExplanation(out, RENT_ASSISTANCE_CURRENT_RATESET);
    // The last line is ESTIMATE_DISCLAIMER (shown at the top of the block).
    return lines.slice(0, -1);
  }
  const out = result.output as AttachCheckOutput;
  // attachBullets ends with ATTACH_DISCLAIMER; drop it (disclaimer is at top).
  const bullets = attachBullets(out).slice(0, -1);
  return [attachHeadline(out), ...bullets];
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
